import React, { useEffect, useState, useRef } from "react";
import { useMemo } from "react";
import { Card, Col, Row, Badge, Form, Modal, Button, Spinner } from "react-bootstrap";
import IconButton from "components/common/iconButton";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTablePagination from "components/common/advance-table/advanceTablePagination";
import AdvanceTableProvider from "providers/advanceTableProvider";
import AdvanceTableSearchBox from "components/common/advance-table/advanceTableSearchBox";
import AdvanceTableFooter from "components/common/advance-table/advanceTableFooter";
import useAdvanceTable from "hooks/useAdvanceTable";
import paths from "routes/paths";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import FalconCloseButton from "components/common/falconCloseButton";
import {
    getInitialPageSize,
    fetchRoleAndClaims,
    handleLoadingAndAccess,
    renderTaskStatusBadge,
    deleteCleanerByOrderDetailId
} from "../../assets/js/common";
import { useAppContext } from "providers/appProvider";
import TaskDrawer from "components/calender/taskDrawer";
import { useSearchParams } from 'react-router-dom';

// Task Delete Confirmation Modal Component
const TaskDeleteModal = ({ show, onClose, onDelete, t }) => {
    return (
        <Modal show={show} onHide={onClose} backdrop="static" contentClassName="border">
            <Modal.Header>
                <Modal.Title>{t("confirmation")}</Modal.Title>
                <FalconCloseButton onClick={onClose} />
            </Modal.Header>

            <Modal.Body>
                {t("confirmDeleteTask")}
            </Modal.Body>

            <Modal.Footer>
                <Button
                    variant="falcon-default"
                    size="sm"
                    onClick={onClose}
                >
                    {t("cancel")}
                </Button>

                <Button
                    variant="danger"
                    size="sm"
                    onClick={onDelete}
                >
                    {t("delete")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const Jobs = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [globalFilter, setGlobalFilter] = useState("");
    const [filterOption, setFilterOption] = useState("today");
    const [isLoading, setIsLoading] = useState(true);
    const [hasAccess, setHasAccess] = useState(false);
    const locationId = localStorage.getItem("LocationId");

    // TaskDrawer states
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [pendingOpenReview, setPendingOpenReview] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [allEmployees, setAllEmployees] = useState([]);
    const userRole = localStorage.getItem("UserRole");
    const [searchParams, setSearchParams] = useSearchParams();

    // Delete Modal states
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedOrderDetailId, setSelectedOrderDetailId] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [displayedRows, setDisplayedRows] = useState([]);

    useEffect(() => {
        fetchRoleAndClaims({
            navigate,
            claimType: "jobs",
            claimValue: "view",
            setHasAccess,
            setIsLoading,
        });
    }, [navigate]);

    const {
        config: { location: userLocation },
    } = useAppContext();

    // Fetch all employees for TaskDrawer
    useEffect(() => {
        const fetchAllEmployees = async () => {
            const selectedLocationId = localStorage.getItem("LocationId");
            const customerId = localStorage.getItem("customerId");

            try {
                const usersResponse = await axios.get(
                    `${process.env.REACT_APP_API_URL}/userRoute/users/allLocations`,
                    {
                        params: { customerId },
                        headers: {
                            Authorization: process.env.REACT_APP_BasicAuthSecret
                        }
                    }
                );

                const usersWithRoles = usersResponse.data.map(user => ({
                    ...user,
                    RoleName: user.Role || "Unknown"
                }));

                const employeesOnly = usersWithRoles.filter(user => {
                    const role = user.RoleName;
                    return (
                        role !== t("clientText") &&
                        !user.IsDelete &&
                        user.Status === "0" &&
                        user.locations?.some(
                            loc => String(loc.LocationId) === String(selectedLocationId)
                        )
                    );
                });

                setAllEmployees(employeesOnly);

            } catch (err) {
                console.error("Error fetching users or roles", err);
            }
        };

        fetchAllEmployees();
    }, [locationId, t]);

    const fetchJobs = async () => {
        if (!userLocation?.LocationId) return;

        setLoading(true);
        try {
            const { startDate, endDate } = getDateRange(filterOption);
            const customerId = localStorage.getItem("customerId");

            if (!locationId || !customerId) {
                console.error("LocationId and CustomerId are missing.");
                return;
            }

            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/orderRoute/full-orders`,
                {
                    params: {
                        locationId: userLocation.LocationId,
                        startDate: startDate.format("YYYY-MM-DD"),
                        endDate: endDate.format("YYYY-MM-DD"),
                        customerId
                    },
                    headers: {
                        Authorization: process.env.REACT_APP_BasicAuthSecret
                    }
                }
            );
            const data = res.data.data || [];
            const rows = buildJobRows(data);
            const filtered = rows.filter(j =>
                moment(j.WeekStartDate).isBetween(startDate, endDate, "day", "[]")
            );

            let finalRows = filtered;

            if (filterOption === "completed") {
                finalRows = filtered.filter(j => Number(j.Status) === 2);
            }

            if (filterOption === "overdue") {
                finalRows = filtered.filter(j =>
                    [0, 1, 3, 4, 5, 6, 7].includes(Number(j.Status))
                );
            }

            finalRows.sort(
                (a, b) =>
                    new Date(b.WeekStartDate).getTime() -
                    new Date(a.WeekStartDate).getTime()
            );

            setTableData(finalRows);
            setDisplayedRows(finalRows);

        } catch (error) {
            console.error("Error fetching jobs:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const orderDetailId = searchParams.get("orderDetailId");
        const openReview = searchParams.get("openReview");

        if (!orderDetailId || openReview !== "true") return;
        if (loading) return;

        const matchedRow = tableData.find(j => j.OrderDetailId === orderDetailId);

        if (matchedRow) {
            setSelectedTask(matchedRow);
            setPendingOpenReview(true);  
            setDrawerOpen(true);
            setSearchParams({}, { replace: true });
        } else if (filterOption === "today") {
            setFilterOption("completed");
        }
    }, [searchParams, tableData, loading, filterOption]);

    const renderStatusBadge = (status, t) => {
        const statusNumberToKey = {
            0: "planned",
            1: "inprogress",
            2: "jobcomplete",
            3: "unabletoaccesslocation",
            4: "customernotavailable",
            5: "equipmentissue",
            6: "cancelledbycustomer",
            7: "incomplete"
        };

        let normalizedStatus = "";

        if (!isNaN(status) && status !== null && status !== "") {
            normalizedStatus = statusNumberToKey[Number(status)] || "";
        } else {
            normalizedStatus = (status || "").toString().replace(/\s+/g, "").toLowerCase();
        }

        const variantMap = {
            planned: "primary",
            inprogress: "info",
            jobcomplete: "success",
            unabletoaccesslocation: "warning",
            customernotavailable: "warning",
            equipmentissue: "warning",
            cancelledbycustomer: "danger",
            incomplete: "warning"
        };

        const variant = variantMap[normalizedStatus] || "info";

        const translatedText = t?.(`orderDetailStatus.${normalizedStatus}`) || status;

        return (
            <Badge pill bg={variant} className="me-2">
                {translatedText}
            </Badge>
        );
    };

    const getDateRange = (filterOption) => {
        switch (filterOption) {
            case "today":
                return {
                    startDate: moment().startOf("day"),
                    endDate: moment().endOf("day")
                };

            case "completed":
            case "overdue":
                return {
                    startDate: moment().subtract(30, "days").startOf("day"),
                    endDate: moment().endOf("day")
                };

            default:
                return {
                    startDate: moment().startOf("day"),
                    endDate: moment().endOf("day")
                };
        }
    };

    const formatTaskTimeRange = (weekStartDate, startTime, endTime) => {
        if (!startTime || !endTime) return "";

        const taskDate = moment(weekStartDate).format("YYYY-MM-DD");
        const start = moment.utc(`${taskDate}T${moment.utc(startTime).format("HH:mm:ss")}`).local();
        const end = moment.utc(`${taskDate}T${moment.utc(endTime).format("HH:mm:ss")}`).local();

        return `${start.format("HH:mm")} - ${end.format("HH:mm")}`;
    };

    const truncateText = (text, maxLength = 12) => {
        if (!text) return "--";
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    };

    useEffect(() => {
        fetchJobs();
    }, [filterOption, userLocation?.LocationId]);

    useEffect(() => {
        if (!globalFilter) {
            setDisplayedRows(tableData);
            return;
        }

        const search = globalFilter.toLowerCase();

        const filtered = tableData.filter(j => {
            const relation = j.Relation || {};

            const locationText = [
                relation.StreetName,
                relation.HouseNumber,
                relation.City,
                relation.PostCode,
                relation.Country
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            const timeText = formatTaskTimeRange(
                j.WeekStartDate,
                j.StartTime,
                j.EndTime
            ).toLowerCase();

            const statusKeyMap = {
                0: "planned",
                1: "inprogress",
                2: "jobcomplete",
                3: "unabletoaccesslocation",
                4: "customernotavailable",
                5: "equipmentissue",
                6: "cancelledbycustomer",
                7: "incomplete"
            };

            const statusKey =
                statusKeyMap[j.Status] ||
                String(j.Status || "").toLowerCase();

            const statusText =
                t(`orderDetailStatus.${statusKey}`)?.toLowerCase() || "";

            return (
                j.ClientName?.toLowerCase().includes(search) ||
                j.CleanerName?.toLowerCase().includes(search) ||
                j.TaskName?.toLowerCase().includes(search) ||
                j.OrderNumber?.toLowerCase().includes(search) ||
                j.ProposalNumber?.toLowerCase().includes(search) ||
                j.WorkType?.toLowerCase().includes(search) ||
                locationText.includes(search) ||
                timeText.includes(search) ||
                statusKey.includes(search) ||
                statusText.includes(search)
            );
        });

        setDisplayedRows(filtered);
    }, [globalFilter, tableData, t]);

    const buildJobRows = (orders) => {
        return orders.flatMap(order =>
            (order.OrderDetails || []).map(detail => {
                return {
                    JobId: detail.OrderDetailId,
                    OrderDetailId: detail.OrderDetailId,
                    Status: detail.Status,
                    WeekStartDate: detail.WeekStartDate,
                    StartTime: detail.StartTime,
                    EndTime: detail.EndTime,
                    Task: detail.Task,
                    TaskName: detail.Task?.TaskName,
                    CleanerName: detail.CleanerName,
                    CleanerId: detail.CleanerId,
                    OrderId: order.OrderId,
                    OrderNumber: order.OrderNumber,
                    ClientName: order.ClientName,
                    Relation: order.Relation,
                    RelationId: order.RelationId,
                    ProposalNumber: order.ProposalNumber,
                    WorkType: order.WorkType,
                    TemplateName: order.TemplateName,
                    ProposalTaskId: detail.ProposalTaskId,
                    DurationMinutes: detail.DurationMinutes,
                    DayOfWeek: detail.DayOfWeek,
                    Subtasks: detail.Subtasks || [],
                };
            })
        );
    };

    const handleRowClick = (row) => {
        setDetailsLoading(true);
        setSelectedTask(row.original);
        setDrawerOpen(true);
        setDetailsLoading(false);
    };
    // Delete handler
    const handleDeleteTask = async () => {
        if (!selectedOrderDetailId) return;

        try {
            await deleteCleanerByOrderDetailId(selectedOrderDetailId, localStorage.getItem("UserId"));

            toast.success(t("taskDeletedSuccessfully"), {
                position: "top-right"
            });

            setShowDeleteModal(false);
            setSelectedOrderDetailId(null);
            setDrawerOpen(false);

            // Refresh jobs list
            await fetchJobs();

        } catch (error) {
            console.error("Failed to delete task:", error);
            toast.error(t("failedToDeleteTask"), {
                position: "top-right"
            });
        }
    };

    const handleRemoveTask = (data) => {
        setSelectedOrderDetailId(data.orderDetailId);
        setShowDeleteModal(true);
    };

    const columns = [
        {
            accessorKey: "ClientName",
            header: t("client"),
            cell: ({ row }) => (
                <div
                    onClick={() => handleRowClick(row)}
                    style={{ cursor: 'pointer' }}
                >
                    {row.original.ClientName}
                </div>
            ),
        },
        {
            header: t("branch"),
            cell: ({ row }) => {
                const r = row.original.Relation;
                return (
                    <div
                        onClick={() => handleRowClick(row)}
                        style={{ cursor: 'pointer' }}
                    >
                        {`${r?.StreetName || ""} ${r?.HouseNumber || ""}, ${r?.City || ""}`}
                    </div>
                );
            },
        },
        {
            header: t("assignCleaner"),
            accessorKey: "CleanerName",
            cell: ({ row }) => (
                <div
                    onClick={() => handleRowClick(row)}
                    style={{ cursor: 'pointer' }}
                >
                    {row.original.CleanerName}
                </div>
            ),
        },
        {
            header: t("plannedStartDate"),
            cell: ({ row }) => (
                <div
                    onClick={() => handleRowClick(row)}
                    style={{ cursor: 'pointer' }}
                >
                    {moment(row.original.WeekStartDate).format("DD-MM-YYYY")}
                </div>
            ),
        },
        {
            header: t("time"),
            cell: ({ row }) => (
                <div
                    onClick={() => handleRowClick(row)}
                    style={{ cursor: 'pointer' }}
                >
                    {formatTaskTimeRange(
                        row.original.WeekStartDate,
                        row.original.StartTime,
                        row.original.EndTime
                    )}
                </div>
            ),
        },
        {
            header: t("task"),
            cell: ({ row }) => (
                <div
                    onClick={() => handleRowClick(row)}
                    style={{ cursor: 'pointer' }}
                    title={row.original.Task?.TaskName || "--"}
                >
                    {truncateText(row.original.Task?.TaskName)}
                </div>
            ),
        },
        {
            header: t("status"),
            cell: ({ row }) => (
                <div
                    onClick={() => handleRowClick(row)}
                    style={{ cursor: 'pointer' }}
                >
                    {renderStatusBadge(row.original.Status, t)}
                </div>
            ),
        },
    ];

    const table = useAdvanceTable({
        data: displayedRows,
        columns,
        selection: false,
        sortable: true,
        pagination: true,
        perPage: getInitialPageSize("jobTable"),
    });

    const loadingOrRedirect = handleLoadingAndAccess(
        isLoading,
        hasAccess,
    );

    if (loadingOrRedirect) {
        return loadingOrRedirect;
    }

    return (
        <>
            <AdvanceTableProvider {...table}>
                <Row className="g-3 mb-3">
                    <Col lg={12}>
                        <Card className="mb-3 h-100">
                            <Background image={corner4} className="bg-card" />
                            <Card.Body className="position-relative pe-3 pb-2">
                                <Row className="flex-between-center">
                                    <Col md>
                                        <h5 className="mb-2 mb-md-0">{t("menu.jobs")}</h5>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Card className="mb-3">
                    <Card.Header>
                        <Row className="align-items-center justify-content-between">
                            <Col
                                xs={12}
                                sm={12}
                                md={12}
                                className="d-flex justify-content-end align-items-center gap-2"
                            >
                                <Form.Select
                                    value={filterOption}
                                    onChange={(e) => {
                                        setFilterOption(e.target.value);
                                    }}
                                    size="sm"
                                    style={{ maxWidth: "180px" }}
                                    disabled={loading}
                                >
                                    <option value="today">{t("todaysJobs")}</option>
                                    <option value="completed">{t("completed")}</option>
                                    <option value="overdue">{t("overdue")}</option>
                                </Form.Select>

                                <div style={{ minWidth: "200px" }}>
                                    <AdvanceTableSearchBox setGlobalFilter={setGlobalFilter} />
                                </div>
                            </Col>
                        </Row>
                    </Card.Header>
                    <Card.Body className="p-0 position-relative">

                        {loading && (
                            <div className="text-center my-5">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">{t("loading")}</span>
                                </Spinner>
                            </div>
                        )}

                        {!loading && displayedRows.length === 0 && (
                            <div className="text-center py-5">
                                {t("noDataAvailableText")}
                            </div>
                        )}

                        {!loading && displayedRows.length > 0 && (
                            <AdvanceTable
                                headerClassName="bg-200 text-nowrap align-middle"
                                rowClassName="align-middle white-space-nowrap"
                                tableProps={{
                                    bordered: true,
                                    striped: true,
                                    size: "sm",
                                    className: "mb-0 overflow-hidden data-font-size table-hover",
                                }}
                            />
                        )}

                    </Card.Body>

                    <Card.Footer>
                        <AdvanceTableFooter
                            rowInfo
                            navButtons
                            rowsPerPageSelection
                            tableKey="jobTable"
                        />
                    </Card.Footer>
                </Card>
            </AdvanceTableProvider>

            {/* Task Delete Confirmation Modal */}
            <TaskDeleteModal
                show={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedOrderDetailId(null);
                }}
                onDelete={handleDeleteTask}
                t={t}
            />

            {/* TaskDrawer Component */}
            <TaskDrawer
                open={drawerOpen}
                onClose={() => {
                    setDrawerOpen(false);
                    setSelectedTask(null);
                    setPendingOpenReview(false);
                }}
                selectedTask={selectedTask}
                loading={detailsLoading}
                t={t}
                renderStatusBadge={renderTaskStatusBadge}
                userRole={userRole}
                disableStartTimeUpdate={false}
                fetchCalendarData={fetchJobs}
                calendarStart={getDateRange(filterOption).startDate.toDate()}
                calendarEnd={getDateRange(filterOption).endDate.toDate()}
                allEmployees={allEmployees}
                onRemoveCleaner={handleRemoveTask}
                context={"jobs"}
                onTaskUpdated={fetchJobs}
                initialSection={pendingOpenReview ? "review" : "overview"}
            />
        </>
    );
};

export default Jobs;